import type {
    IIndexedValidationError,
    IPagination,
} from "@common-shared/types/general.types"
import { IOneVerifyGetNetworkStatusRulesListRequestData } from "@common-shared/types/requestData/oneVerifyRequestData.types"
import type {
    INetworkStatusRule,
    INetworkStatusRulesListResult,
    INetworkStatusRulesVersion,
    IPractice,
} from "@common-shared/types/responseData/commonResponseData.types"

import { API_ROUTES } from "../config/routes.config"
import type { RawNetworkStatusRuleCsvRowDTO } from "../dto/rawNetworkStatusRuleCsvInput.dto"
import { HttpHelper } from "../helpers/http.helper"
import { UtilHelper } from "../helpers/util.helper"
import type { INetworkStatusRulesFilters } from "../types/networkStatus.types"

export class NetworkStatusService {
    /**
     * Get paginated list of network status rules for practice and version
     */
    static async getRulesList(
        practice: IPractice | number,
        version: number,
        pagination: IPagination,
        filters?: INetworkStatusRulesFilters,
        abortSignal?: AbortSignal,
    ): Promise<INetworkStatusRulesListResult | undefined> {
        // Omit display-only payer/thirdPartyAdministrator objects (not sent to API)
        const {
            payer: _payer,
            thirdPartyAdministrator: _thirdPartyAdministrator,
            ...apiFilters
        } = filters ?? {}

        const requestData: IOneVerifyGetNetworkStatusRulesListRequestData = {
            version,
            start: pagination.start,
            count: pagination.count,

            ...apiFilters,
        }

        const result = await HttpHelper.sendRequest(
            API_ROUTES.NETWORK_STATUS_RULES_GET_LIST.replace(
                ":practiceId",
                (typeof practice === "number"
                    ? practice
                    : practice.id
                ).toString(),
            ),
            {
                ...requestData,

                // also some data that are objects need stringifying (e.g. date ranges)
                startDate: requestData.startDate
                    ? JSON.stringify(requestData.startDate)
                    : undefined,
                endDate: requestData.endDate
                    ? JSON.stringify(requestData.endDate)
                    : undefined,
            },
            "GET",
            undefined,
            undefined,
            undefined,
            undefined,
            abortSignal,
        )

        return result && !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Validate network status rules. Body: { rules }.
     * Returns { success: [], errors } with errors as { index, error } per failed row.
     * Same response shape as createRules; use before create to show validation errors in the UI.
     */
    static async validateRules(
        practice: IPractice,
        body: { rules: RawNetworkStatusRuleCsvRowDTO[] },
    ): Promise<
        | {
              errors: IIndexedValidationError[]
          }
        | undefined
    > {
        const url = API_ROUTES.NETWORK_STATUS_RULES_VALIDATE.replace(
            ":practiceId",
            practice.id.toString(),
        )

        const result = await HttpHelper.sendRequest(
            url,
            { rules: body.rules },
            "POST",
        )

        return result && !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Create network status rules (bulk). Body: { versionId?: number, rules }.
     * Returns { success, errors } with errors as { index, error } per failed row.
     * Returns undefined when the request itself fails; HttpHelper has toasted the API error by then.
     */
    static async createRules(
        practice: IPractice,
        body: {
            versionId?: number
            rules: RawNetworkStatusRuleCsvRowDTO[]
        },
    ): Promise<
        | {
              success?: INetworkStatusRule[]
              errors?: IIndexedValidationError[]
              /** Set when a new version was created (first chunk); use for subsequent chunks. */
              versionId?: number
          }
        | undefined
    > {
        const url = API_ROUTES.NETWORK_STATUS_RULES_CREATE.replace(
            ":practiceId",
            practice.id.toString(),
        )

        const result = await HttpHelper.sendRequest(url, body, "POST")

        return result && !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Get all network status rules versions for the practice, ordered by version ASC.
     */
    static async getVersions(
        practice: IPractice,
    ): Promise<INetworkStatusRulesVersion[] | undefined> {
        const result = await HttpHelper.sendRequest(
            API_ROUTES.NETWORK_STATUS_VERSIONS_GET.replace(
                ":practiceId",
                practice.id.toString(),
            ),
        )

        if (!result || result.errorData || !Array.isArray(result.items)) {
            return undefined
        }

        return result.items
    }

    /**
     * Set the practice's active network status version.
     * Body: { versionId: number }. Returns { success: true } on success.
     */
    static async setActiveVersion(
        practice: IPractice,
        versionId: number,
    ): Promise<{ success: boolean } | undefined> {
        const result = await HttpHelper.sendRequest(
            API_ROUTES.NETWORK_STATUS_ACTIVE_VERSION_PUT.replace(
                ":practiceId",
                practice.id.toString(),
            ),
            { versionId },
            "PUT",
        )

        return result && !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }
}
